import { AnchorButton, Classes, Intent, Navbar } from '@blueprintjs/core';
import { createSelector } from '@reduxjs/toolkit';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { usePortalFeatures } from 'vet-bones/utils/portal';
import { usePortalVoiceboxConversation } from 'vet-bones/utils/voicebox';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { ProjectResourceType } from 'src/constants/project';
import { TEST_IDS } from 'src/constants/testIds';
import { NodeCategory } from 'src/containers/ProjectDiagram/constants/categories';
import {
  isDeletedLinkData,
  isDeletedNodeData,
} from 'src/containers/ProjectDiagram/utils/data';
import {
  diagramSnapshotSelector,
  isDraftDiagramSelector,
} from 'src/selectors/diagramSnapshot';
import { projectSelector } from 'src/selectors/project';
import { projectCompetencyQuestionsSelector } from 'src/selectors/projectCompetencyQuestions';
import * as copy from 'src/templates/copy';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';

const conversationIdSelector = (projectId: string) =>
  createSelector(
    projectSelector(projectId),
    (project) => project?.voiceboxConversationId || ''
  );

export const VoiceboxFooter = () => {
  const dispatch = useReduxDispatch();
  const isMounted = useIsMounted();
  const { projectId } = useRouterParams();

  const diagram = useReduxSelector(diagramSnapshotSelector(projectId));
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));
  const conversationId = useReduxSelector(conversationIdSelector(projectId));
  const conversation = usePortalVoiceboxConversation(conversationId, {
    enabled: false,
  });

  const portalFeatures = usePortalFeatures();
  const isVoiceboxEnabled = useIsVoiceboxEnabled();
  const project = useReduxSelector(projectSelector(projectId));
  const useVoiceboxSuggestions =
    Boolean(isVoiceboxEnabled && portalFeatures.voiceboxSuggestions) &&
    !project?.disableVoiceboxSuggestions;

  const questions = useReduxSelector(
    projectCompetencyQuestionsSelector(projectId)
  );
  const { cancelEvaluateSummary, evaluateSummary } = useEvaluateSummary();

  const handleAcceptVoicebox = async () => {
    if (!isDraftDiagram || !diagram) {
      return;
    }

    const updatedModel = cloneDeep(diagram.model);
    updatedModel.nodeDataArray = updatedModel.nodeDataArray.filter(
      (data) => !isDeletedNodeData(data)
    );
    updatedModel.linkDataArray = updatedModel.linkDataArray
      .filter((data) => !isDeletedLinkData(data))
      .map((link) => ({
        ...link,
        points: [],
      }));
    updatedModel.modelData.draftData = undefined;

    const { data } = await conversation?.refetch();
    if (!isMounted.current) {
      return;
    }

    const isSummaryOutdated =
      !project?.voiceboxEvaluationSummary ||
      !project?.voiceboxEvaluatedSummary ||
      questions.some(
        (question) =>
          !question.evaluation ||
          !project.voiceboxEvaluatedCompetencyQuestions?.includes(question.id)
      );

    if (isSummaryOutdated && useVoiceboxSuggestions) {
      evaluateSummary(project);
    }

    dispatch(
      projectsActionCreators.acceptedProjectDraftDiagram(
        projectId,
        { id: uuid(), model: updatedModel },
        data?.messages || []
      )
    );
  };

  const handleRejectVoicebox = async () => {
    if (!isDraftDiagram || !diagram) {
      return;
    }

    const updatedModel = cloneDeep(diagram.model);
    if (!updatedModel.modelData.draftData) {
      return;
    }

    const prevNodeDataArray =
      updatedModel.modelData.draftData.prevNodeDataArray || [];
    const prevFileUploadIds = new Set(
      prevNodeDataArray
        .filter(
          (node) =>
            node.category === NodeCategory.RESOURCE &&
            node.type === ProjectResourceType.FILE_UPLOAD
        )
        .map((node) => node.value)
    );

    const currentFileUploadIds = diagram.model.nodeDataArray
      .filter(
        (node) =>
          node.category === NodeCategory.RESOURCE &&
          node.type === ProjectResourceType.FILE_UPLOAD
      )
      .map((node) => node.value);

    const fileUploadsToRemove = currentFileUploadIds.filter(
      (id) => !prevFileUploadIds.has(id)
    );

    updatedModel.nodeDataArray = (
      updatedModel.modelData.draftData.prevNodeDataArray || []
    ).filter((data) => !isDeletedNodeData(data));
    updatedModel.linkDataArray = (
      updatedModel.modelData.draftData.prevLinkDataArray || []
    ).filter((data) => !isDeletedLinkData(data));
    updatedModel.modelData = updatedModel.modelData.draftData.prevModelData;

    const { data } = await conversation?.refetch();
    if (!isMounted.current) {
      return;
    }

    // cancel any pending model summaries when preview state is closed
    cancelEvaluateSummary();

    dispatch(
      projectsActionCreators.clearedProjectVoiceboxEvaluation({
        id: projectId,
        clearSummary: true,
      })
    );

    dispatch(
      projectsActionCreators.rejectedProjectDraftDiagram(
        projectId,
        { id: uuid(), model: updatedModel },
        data?.messages || [],
        fileUploadsToRemove.length > 0 ? fileUploadsToRemove : undefined
      )
    );
  };

  if (!isDraftDiagram) {
    return null;
  }

  return (
    <Navbar
      className="sd-voicebox-footer"
      data-testid={TEST_IDS.voicebox.footer.container}
    >
      <Navbar.Group
        className={classNames(Classes.DARK, 'sd-voicebox-footer--center')}
      >
        <div>{copy.components.voicebox.footer.help}</div>
      </Navbar.Group>
      <Navbar.Group className="sd-voicebox-footer--right">
        <div className="sd-voicebox-footer__reject">
          <AnchorButton
            data-testid={TEST_IDS.voicebox.footer.reject}
            intent={Intent.PRIMARY}
            minimal
            onClick={handleRejectVoicebox}
            outlined
            text={copy.components.voicebox.footer.reject}
          />
        </div>
        <AnchorButton
          data-testid={TEST_IDS.voicebox.footer.accept}
          intent={Intent.PRIMARY}
          onClick={handleAcceptVoicebox}
          text={copy.components.voicebox.footer.accept}
        />
      </Navbar.Group>
    </Navbar>
  );
};
